import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Save, Lock, AlertCircle, Plus } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { useAuth } from "../../context/AuthContext";
import { api, apiErrorField, fmtDate, fmtDuration, fmtEur } from "../../lib/api";
import { validate } from "../../lib/validate";
import { Field, inputProps, OptionList } from "../site/Primitives";
import { PhoneOptinFields, phoneRules, toPayloadPhone } from "./PhoneOptin";
import { InvoiceButton, IvStatus } from "./ExpertMissions";
import { VerifiedBadge } from "../site/VerifiedBadge";

const EMPTY = { company: "", contact_name: "", vertical: "", site: "", country: "", phone: "", whatsapp_optin: false };
const RULES = { company: ["required", ["min", 2]], contact_name: ["required", ["min", 2]], vertical: ["required"], country: ["required", ["min", 2]] };
const STATUS_C = { new: "var(--amber)", in_progress: "var(--cyan)", resolved: "#6FCF97", closed: "var(--text-2)" };

const ClientProfile = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const c = t.pro.client;
  const d = t.dossier;
  const [values, setValues] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ sending: false, saved: false, failed: false });

  useEffect(() => {
    api.get("/me/client").then(({ data }) => {
      if (data) setValues({ ...EMPTY, ...data, site: data.site || "", phone: data.phone || "" });
      else setValues((v) => ({ ...v, contact_name: user.name || "" }));
      setLoaded(true);
    });
  }, [user]);

  const setVal = (k, v) => { setValues((s) => ({ ...s, [k]: v })); setErrors((er) => ({ ...er, [k]: undefined })); };
  const f = (k, req = true) => ({ ...inputProps(`client-${k}`, errors[k], req), value: values[k], onChange: (e) => setVal(k, e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values, { ...RULES, ...phoneRules(values) });
    setErrors(errs);
    if (Object.keys(errs).length) return document.getElementById(`client-${Object.keys(errs)[0]}`)?.focus();
    setStatus({ sending: true, saved: false, failed: false });
    try {
      const { company, contact_name, vertical, site, country, whatsapp_optin } = values;
      await api.put("/me/client", { company, contact_name, vertical, country, whatsapp_optin, site: site || null, phone: toPayloadPhone(values.phone) });
      setStatus({ sending: false, saved: true, failed: false });
    } catch (err) {
      setErrors(apiErrorField(err));
      setStatus({ sending: false, saved: false, failed: true });
    }
  };

  if (!loaded) return <div className="panel pro-panel"><Loader2 className="spin" aria-label={t.common.loading} /></div>;
  return (
    <div className="panel pro-panel" data-testid="client-profile-panel">
      <h3 className="sub-title" style={{ marginBottom: 8 }}>{d.clientTitle} — {d.profile}</h3>
      <p className="pro-intro">{values.company ? c.intro : d.noProfileClient}</p>
      <form onSubmit={submit} noValidate data-testid="client-profile-form">
        <div className="f-grid">
          <Field id="client-company" label={c.company} error={errors.company} required><input type="text" autoComplete="organization" {...f("company")} /></Field>
          <div className="f-field"><span className="f-label">{d.emailLocked}</span><div className="rate-display" style={{ color: "var(--text-1)" }} data-testid="client-email-display">{user.email}</div></div>
          <Field id="client-contact_name" label={c.contact} error={errors.contact_name} required><input type="text" autoComplete="name" {...f("contact_name")} /></Field>
          <Field id="client-vertical" label={c.vertical} error={errors.vertical} required><select {...f("vertical")}><OptionList map={t.verticals} withEmpty /></select></Field>
          <Field id="client-site" label={c.site} error={errors.site}><input type="text" {...f("site", false)} /></Field>
          <Field id="client-country" label={c.country} error={errors.country} required><input type="text" autoComplete="country-name" {...f("country")} /></Field>
          <PhoneOptinFields prefix="client" values={values} errors={errors} setVal={setVal} />
        </div>
        <p className="info-note"><Lock aria-hidden="true" /><span>{c.payNote}</span></p>
        <div className="f-actions">
          <span />
          <button type="submit" className="btn btn-primary" disabled={status.sending} data-testid="client-profile-submit-btn">
            {status.sending ? <Loader2 className="spin" aria-hidden="true" /> : <Save aria-hidden="true" />}{d.saveProfile}
          </button>
        </div>
      </form>
      {status.saved && <p className="success-msg" role="status" data-testid="client-profile-success"><CheckCircle2 aria-hidden="true" />{d.saved}</p>}
      {status.failed && <p className="error-banner" role="alert"><AlertCircle aria-hidden="true" />{Object.keys(errors).length ? t.errors.fixErrors : t.errors.generic}</p>}
    </div>
  );
};

const ClientRequests = ({ rows }) => {
  const { t, lang } = useLang();
  const h = t.pro.client.headers;
  if (!rows.length) return <p className="empty" data-testid="client-requests-empty">{t.pro.client.noRequests}</p>;
  return (
    <div className="table-wrap"><table className="data-table" data-testid="client-requests-table">
      <thead><tr><th>{h.ref}</th><th>{h.date}</th><th>{h.need}</th><th>{h.urgency}</th><th>{t.dossier.assignedExpert}</th><th>{h.status}</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id} data-testid={`client-request-row-${r.ref}`}>
          <td className="mono strong">{r.ref}</td><td className="mono">{fmtDate(r.created_at, lang)}</td>
          <td>{t.needs[r.need]}</td><td>{t.urgencies[r.urgency]}</td>
          <td>{r.assigned_expert_name ? <>{r.assigned_expert_name}<VerifiedBadge compact since={r.assigned_expert_verified_since} testid={`client-request-verified-${r.ref}`} /></> : <span style={{ color: "var(--text-2)" }}>{t.dossier.pending}</span>}</td>
          <td><span className="pill" style={{ "--c": STATUS_C[r.status], "--ct": STATUS_C[r.status] }}>{t.statuses[r.status]}</span></td>
        </tr>
      ))}</tbody>
    </table></div>
  );
};

export const ClientInterventions = ({ rows }) => {
  const { t, lang } = useLang();
  const h = t.pro.client.headers;
  if (!rows.length) return <p className="empty" data-testid="client-interventions-empty">{t.pro.client.noInterventions}</p>;
  return (
    <div className="table-wrap"><table className="data-table" data-testid="client-interventions-table">
      <thead><tr><th>{h.ref}</th><th>{h.date}</th><th>{h.expert}</th><th>{h.duration}</th><th>{h.amount}</th><th>{h.status}</th><th>{h.invoice}</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id} data-testid={`client-intervention-row-${r.ref}`}>
          <td className="mono strong">{r.ref}</td><td className="mono">{fmtDate(r.assigned_at, lang)}</td><td>{r.expert_name}<VerifiedBadge compact since={r.expert_verified_since} testid={`client-intervention-verified-${r.ref}`} /></td>
          <td className="mono">{fmtDuration(r.duration_seconds)}</td><td className="mono">{fmtEur(r.amount_ht, lang)}</td>
          <td><IvStatus status={r.status} /></td>
          <td>{r.status === "completed" ? <InvoiceButton iv={r} /> : "—"}</td>
        </tr>
      ))}</tbody>
    </table></div>
  );
};

export const useClientFiles = () => {
  const [data, setData] = useState({ requests: null, interventions: null });
  useEffect(() => {
    Promise.all([api.get("/me/requests"), api.get("/me/interventions")])
      .then(([r, i]) => setData({ requests: r.data, interventions: i.data }));
  }, []);
  return data;
};

export const ClientActivity = ({ showRequests = true }) => {
  const { t } = useLang();
  const d = t.dossier;
  const { requests, interventions } = useClientFiles();
  if (!requests) return <div className="panel pro-panel"><Loader2 className="spin" aria-label={t.common.loading} /></div>;
  return (
    <div className="panel pro-panel" data-testid="client-activity-panel">
      {showRequests && (
        <>
          <div className="sub-head">
            <h3 className="sub-title">{d.requests} ({requests.length})</h3>
            <a href="/#contact" className="btn btn-ghost btn-sm" data-testid="client-new-request-btn"><Plus aria-hidden="true" />{d.newRequest}</a>
          </div>
          <ClientRequests rows={requests} />
        </>
      )}
      <h3 className="sub-title" style={{ marginTop: showRequests ? 30 : 0 }}>{d.interventions} ({interventions.length})</h3>
      <ClientInterventions rows={interventions} />
    </div>
  );
};

export const ClientDossier = () => (
  <div className="split-2">
    <ClientProfile />
    <ClientActivity />
  </div>
);
